import { useState, useEffect, useCallback } from 'react'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { toast } from 'react-toastify'

const useOrganizationsViewModel = () => {
    
    const [loading, setLoading] = useState(true)
    const [newOrgName, setNewOrgName] = useState('')
    const [joinOrgName, setJoinOrgName] = useState('')
    const [pendingDeleteOrg, setPendingDeleteOrg] = useState(null)
    const [pendingDeleteOrgJoinRequest, setPendingDeleteOrgJoinRequest] = useState()
    const [orgCounts, setOrgCounts] = useState(null)
    
    const organizations = useWireValue(store.organizations)
    
    const onCreateOrgClick = useCallback(async () => {
        
        try {
            await actions.createOrg(newOrgName)
            toast.success('Created new organization')
            setNewOrgName('')
        } catch (e) {
            console.error('onCreateOrgClick', e)
            toast.error('Failed to create new organization')
        }
        
    }, [newOrgName])
    
    const onJoinOrgClick = useCallback(async () => {
        
        try {
            await actions.requestToJoinOrg(joinOrgName)
            toast.success('Sent request to join organization')
            setJoinOrgName('')
        } catch (e) {
            console.error('onJoinOrgClick', e)
            
            toast.error(e.response.data.error)
        }
        
    }, [joinOrgName])
    
    const onConfirmDeleteClick = useCallback(async () => {
        
        if (organizations.length === 1)
            return toast.error('Failed to delete organization, unable to delete your only organization!')
        
        try {
            await actions.deleteOrg(pendingDeleteOrg.id)
            await actions.fetchOrgs()
            toast.success(`Deleted organization "${pendingDeleteOrg.name}"`)
        } catch (e) {
            console.error('onDeleteClick', e)
            toast.error('Failed to delete organization')
        }
        
    }, [pendingDeleteOrg])
    
    const onConfirmDeleteJoinRequestClick = useCallback(async () => {
        
        try {
            await actions.deleteOrganizationJoinRequest(
                pendingDeleteOrgJoinRequest.organization.id,
                pendingDeleteOrgJoinRequest.id,
            )
            await actions.fetchOrgs()
            toast.success(`Deleted join request for ${pendingDeleteOrgJoinRequest.requester.email}`)
        } catch (e) {
            console.error('onConfirmDeleteJoinRequestClick', e)
            toast.error('Failed to delete join request. Please try again.')
        }
        
    }, [pendingDeleteOrgJoinRequest])
    
    const checkOrgExists = useCallback(async orgName => {
        
        try {
            return await actions.checkOrgExists(orgName)
        } catch (e) {
            console.error('checkOrgExists', e)
            return false
        }
        
    }, [])
    
    const fetchOrgCounts = useCallback(async orgId => {
        
        try {
            const data = await actions.getCountCollectionAndRunsheet(orgId)
            
            setOrgCounts(data)
        } catch (e) {
            console.error('fetchOrgCounts', e)
            setOrgCounts(null)
        }
        
    }, [])
    
    
    useEffect(() => {
        
        actions.fetchOrgs()
            .finally(() => setLoading(false))
        
    }, [])
    
    useEffect(() => {
        
        if (pendingDeleteOrg) {
            fetchOrgCounts(pendingDeleteOrg.id)
        }
        
    }, [pendingDeleteOrg, fetchOrgCounts])
    
    
    return {
        loading,
        newOrgName,
        setNewOrgName,
        joinOrgName,
        setJoinOrgName,
        pendingDeleteOrg,
        setPendingDeleteOrg,
        pendingDeleteOrgJoinRequest,
        setPendingDeleteOrgJoinRequest,
        orgCounts,
        
        organizations,
        
        onCreateOrgClick,
        onJoinOrgClick,
        onConfirmDeleteClick,
        onConfirmDeleteJoinRequestClick,
        checkOrgExists,
    }
    
}

export default useOrganizationsViewModel
